import { Navigate, Route, Routes, useNavigate } from 'react-router-dom'
import { useState, useEffect, useCallback } from 'react'
import { PlusCircleIcon } from '@heroicons/react/24/outline'
import { useAuth } from '../hooks/useAuth'
import { useApplications } from '../hooks/useApplications'
import { AddApplicationForm } from './AddApplicationForm'
import { ApplicationDetailPageWrapper } from './ApplicationDetailPageWrapper'
import { ApplicationsList } from './ApplicationsList'
import { EditExtractedDataModal } from './EditExtractedDataModal'
import { ProfilePage } from './ProfilePage'
import { SettingsPage } from './SettingsPage'
import { BillingPage } from './BillingPage'
import { PricingPage } from './PricingPage'
import { ResumePage } from './ResumePage'
import { ResumeAnalysisDetailPage } from './ResumeAnalysisDetailPage'
import { ResumeComparisonDetailPage } from './ResumeComparisonDetailPage'
import { QuizPage } from './QuizPage'
import { QuizResultPage } from './QuizResultPage'
import { AboutPage } from './AboutPage'
import { AnalyticsPage } from './AnalyticsPage'
import { AIChatPage } from './AIChatPage'
import { ExtensionConnectPage } from './ExtensionConnectPage'
import { ExtensionCallbackPage } from './ExtensionCallbackPage'
import { HelpPage } from './HelpPage'
import { MockInterviewPage } from './MockInterviewPage'
import { MockInterviewResultPage } from './MockInterviewResultPage'
import { ResumeBuilderListPage } from './resumeBuilder/ResumeBuilderListPage'
import { ResumeBuilderEditorPage } from './resumeBuilder/ResumeBuilderEditorPage'
import { StatsSection } from './dashboard/StatsSection'
import { DashboardFiltersToolbar } from './dashboard/DashboardFiltersToolbar'
import { CollapsibleSection } from './dashboard/CollapsibleSection'
import { FollowUpsSection } from './dashboard/FollowUpsSection'
import { ActivationCards } from './dashboard/ActivationCards'
import { SetFollowUpModal } from './SetFollowUpModal'
import { fetchUserAnalyses } from '../services/resumeService'
import { fetchUserQuizCount } from '../services/quizService'
import {
  fetchFollowUps,
  createFollowUp,
  updateFollowUp as updateFollowUpDb,
  deleteFollowUp,
  markFollowUpComplete,
} from '../services/followUpsService'
import type { FollowUp, FollowUpType } from '../types'

type AppRoutesProps = {
  onStartProductTour: () => void
}

export function AppRoutes({ onStartProductTour }: AppRoutesProps) {
  const navigate = useNavigate()
  const {
    sessionUserId,
    sessionEmail,
    avatarUrl,
    handleDeleteAccount,
    setAvatarFromPath,
  } = useAuth()

  const [resumeAnalysesCount, setResumeAnalysesCount] = useState(0)
  const [quizzesCount, setQuizzesCount] = useState(0)

  // Follow-ups state
  const [followUps, setFollowUps] = useState<FollowUp[]>([])
  const [isLoadingFollowUps, setIsLoadingFollowUps] = useState(false)
  const [isFollowUpModalOpen, setIsFollowUpModalOpen] = useState(false)
  const [followUpModalApp, setFollowUpModalApp] = useState<typeof applications[0] | null>(null)
  const [editingFollowUp, setEditingFollowUp] = useState<FollowUp | null>(null)

  // Fetch total resume analyses count
  useEffect(() => {
    if (sessionUserId) {
      fetchUserAnalyses(sessionUserId)
        .then(analyses => setResumeAnalysesCount(analyses.length))
        .catch(err => {
          console.error('Failed to fetch resume analyses count:', err)
          setResumeAnalysesCount(0)
        })
    }
  }, [sessionUserId])

  // Fetch total quizzes count
  useEffect(() => {
    if (sessionUserId) {
      fetchUserQuizCount(sessionUserId)
        .then(count => setQuizzesCount(count))
        .catch(err => {
          console.error('Failed to fetch quizzes count:', err)
          setQuizzesCount(0)
        })
    }
  }, [sessionUserId])

  const {
    applications,
    settings,
    subscription,
    isLoadingApps,
    appsLoadError,
    isLoadingSettings,
    isSavingSettings,
    settingsError,
    effectivePlan,
    isPro,
    activeApps,
    viewMode,
    companyFilter,
    displayMode,
    sortKey,
    sortDir,
    statusFilter,
    appsPage,
    totalPages,
    filteredApps,
    paginatedApps,
    statItems,
    statusLabels,
    availableStatusFilters,
    formUrl,
    isSubmitting,
    formError,
    extractedData,
    isEditModalOpen,
    isSavingApp,
    openRejectId,
    rejectState,
    handleAddApplication,
    handleManualAdd,
    handleConfirmExtractedData,
    toggleStage,
    updateStages,
    updateStatus,
    rejectApplication,
    archiveApplication,
    unarchiveApplication,
    updateNote,
    updateDetails,
    toggleFavorite,
    updateCompensation,
    handleSaveSettings,
    refreshSubscription,
    setViewMode,
    setCompanyFilter,
    setSortKey,
    setSortDir,
    setStatusFilter,
    setAppsPage,
    setFormUrl,
    setOpenRejectId,
    setRejectState,
    handleCancelDraft,
    handleDisplayModeChange,
  } = useApplications()

  // Fetch follow-ups
  const loadFollowUps = useCallback(async () => {
    if (!sessionUserId) return
    setIsLoadingFollowUps(true)
    try {
      const data = await fetchFollowUps(sessionUserId)
      setFollowUps(data)
    } catch (err) {
      console.error('Failed to fetch follow-ups:', err)
    } finally {
      setIsLoadingFollowUps(false)
    }
  }, [sessionUserId])

  useEffect(() => {
    loadFollowUps()
  }, [loadFollowUps])

  // Follow-up handlers
  const handleAddFollowUp = (applicationId?: string) => {
    const app = applicationId ? applications.find(a => a.id === applicationId) : null
    setFollowUpModalApp(app ?? null)
    setEditingFollowUp(null)
    setIsFollowUpModalOpen(true)
  }

  const handleEditFollowUp = (followUp: FollowUp) => {
    const app = applications.find(a => a.id === followUp.applicationId)
    setFollowUpModalApp(app ?? null)
    setEditingFollowUp(followUp)
    setIsFollowUpModalOpen(true)
  }

  const handleFollowUpConfirm = async (data: {
    applicationId: string
    dueAt: string
    title: string
    type: FollowUpType
    note?: string
    followUpId?: string
  }) => {
    if (!sessionUserId) return
    try {
      if (data.followUpId) {
        const updated = await updateFollowUpDb(data.followUpId, {
          dueAt: data.dueAt,
          title: data.title,
          type: data.type,
          note: data.note ?? null,
        })
        setFollowUps(prev => prev.map(fu => fu.id === updated.id ? updated : fu))
      } else {
        const created = await createFollowUp(sessionUserId, data.applicationId, {
          dueAt: data.dueAt,
          title: data.title,
          type: data.type,
          note: data.note,
        })
        setFollowUps(prev => [...prev, created])
      }
    } catch (err) {
      console.error('Failed to save follow-up:', err)
    }
    setIsFollowUpModalOpen(false)
    setFollowUpModalApp(null)
    setEditingFollowUp(null)
  }

  const handleMarkFollowUpDone = async (followUpId: string) => {
    try {
      await markFollowUpComplete(followUpId)
      setFollowUps(prev => prev.filter(fu => fu.id !== followUpId))
    } catch (err) {
      console.error('Failed to mark follow-up as done:', err)
    }
  }

  const handleDeleteFollowUp = async (followUpId: string) => {
    try {
      await deleteFollowUp(followUpId)
      setFollowUps(prev => prev.filter(fu => fu.id !== followUpId))
    } catch (err) {
      console.error('Failed to delete follow-up:', err)
    }
  }

  const PAGE_SIZE = displayMode === 'cards' ? 12 : 10
  const safePage = Math.min(appsPage, totalPages)
  const realApplicationsCount = applications.filter((app) => !app.company.startsWith('Demo: ')).length
  const focusAddJob = () => {
    navigate('/')
    window.setTimeout(() => {
      const input = document.querySelector('[data-tour="add-job"] input')
      if (input instanceof HTMLInputElement) {
        input.focus()
      }
    }, 120)
  }

  return (
    <Routes>
      <Route
        path="/"
        element={
          <>
            {/* Dashboard header: Add form (55%) + Stats (45%) */}
            <CollapsibleSection
              title="Add New Application"
              icon={PlusCircleIcon}
              storageKey="dashboard-add-stats-expanded"
              defaultExpanded={false}
              mobileOnly
            >
              <section className="grid gap-3 lg:gap-4 lg:grid-cols-[11fr_9fr]">
                <AddApplicationForm
                  formUrl={formUrl}
                  isSubmitting={isSubmitting}
                  formError={formError}
                  onSubmit={handleAddApplication}
                  onChangeUrl={setFormUrl}
                  onManualAdd={handleManualAdd}
                />
                <div data-tour="dashboard-stats">
                  <StatsSection statItems={statItems} isLoadingApps={isLoadingApps} isPro={isPro} compact />
                </div>
              </section>
            </CollapsibleSection>

            {settings.onboardingCompleted && (
              <ActivationCards
                applicationsCount={applications.length}
                realApplicationsCount={realApplicationsCount}
                resumeAnalysesCount={resumeAnalysesCount}
                productTourCompleted={settings.productTourCompleted}
                onFocusAddJob={focusAddJob}
                onStartProductTour={onStartProductTour}
              />
            )}

            {/* Mobile: Follow-ups above table */}
            <div className="lg:hidden" data-tour="follow-ups">
              <FollowUpsSection
                followUps={followUps}
                onMarkDone={handleMarkFollowUpDone}
                onDelete={handleDeleteFollowUp}
                onEdit={handleEditFollowUp}
                onAddNew={handleAddFollowUp}
                isLoading={isLoadingFollowUps}
              />
            </div>

            {/* Main content: Applications (left) + Follow-ups sidebar (right on desktop) */}
            <div className="flex flex-col lg:flex-row lg:items-start gap-4">
              {/* Applications list section */}
              <div className="flex-1 min-w-0 space-y-3 -mt-3 md:mt-0" data-tour="applications-list">
                <DashboardFiltersToolbar
                  viewMode={viewMode}
                  onViewModeChange={setViewMode}
                  companyFilter={companyFilter}
                  onCompanyFilterChange={setCompanyFilter}
                  statusFilter={statusFilter}
                  onStatusFilterChange={setStatusFilter}
                  availableStatusFilters={availableStatusFilters}
                  statusLabels={statusLabels}
                  displayMode={displayMode}
                  onDisplayModeChange={handleDisplayModeChange}
                  sortKey={sortKey}
                  sortDir={sortDir}
                  onSortChange={(key, dir) => {
                    setSortKey(key)
                    setSortDir(dir)
                  }}
                  collapsible
                />

                {appsLoadError && (
                  <div className="rounded-lg border border-rose-200 dark:border-rose-900/40 bg-rose-50 dark:bg-rose-950/40 px-3 py-2 text-sm text-rose-700 dark:text-rose-100">
                    {appsLoadError}
                  </div>
                )}

                <ApplicationsList
                  applications={paginatedApps}
                  isLoading={isLoadingApps}
                  onToggleStage={toggleStage}
                  onUpdateStages={updateStages}
                  onUpdateCompensation={updateCompensation}
                  onUpdateStatus={updateStatus}
                  onStartReject={(id) => {
                    setOpenRejectId(id)
                    setRejectState({ code: 'too_expensive' })
                  }}
                  onSubmitReject={rejectApplication}
                  onCancelReject={() => setOpenRejectId(null)}
                  onArchive={archiveApplication}
                  onUnarchive={unarchiveApplication}
                  onUpdateNote={updateNote}
                  onSetFollowUp={handleAddFollowUp}
                  openRejectId={openRejectId}
                  rejectState={rejectState}
                  setRejectState={(reason) => {
                    setRejectState(reason)
                  }}
                  viewMode={viewMode}
                  displayMode={displayMode}
                  page={safePage}
                  totalPages={totalPages}
                  pageSize={PAGE_SIZE}
                  totalItems={filteredApps.length}
                  onPageChange={(next: number) => setAppsPage(Math.min(Math.max(1, next), totalPages))}
                  settings={settings}
                  onToggleFavorite={toggleFavorite}
                  onSettingsChange={handleSaveSettings}
                  plan={effectivePlan}
                  sortKey={sortKey}
                  sortDir={sortDir}
                />
              </div>

              {/* Desktop: Follow-ups sidebar */}
              <aside className="hidden lg:block lg:w-[380px] lg:flex-shrink-0" data-tour="follow-ups">
                <div className="sticky top-4">
                  <FollowUpsSection
                    followUps={followUps}
                    onMarkDone={handleMarkFollowUpDone}
                    onDelete={handleDeleteFollowUp}
                    onEdit={handleEditFollowUp}
                    onAddNew={handleAddFollowUp}
                    isLoading={isLoadingFollowUps}
                  />
                </div>
              </aside>
            </div>


            {/* Edit extracted data modal */}
            <EditExtractedDataModal
              isOpen={isEditModalOpen}
              extraction={extractedData}
              onConfirm={handleConfirmExtractedData}
              onCancel={() => {
                handleCancelDraft()
                setFormUrl('')
              }}
              isLoading={isSavingApp}
            />

            {/* Set follow-up modal */}
            <SetFollowUpModal
              isOpen={isFollowUpModalOpen}
              application={followUpModalApp}
              applications={applications}
              editingFollowUp={editingFollowUp}
              onConfirm={handleFollowUpConfirm}
              onCancel={() => {
                setIsFollowUpModalOpen(false)
                setFollowUpModalApp(null)
                setEditingFollowUp(null)
              }}
            />
          </>
        }
      />
      <Route
        path="/app/:id"
        element={
          <ApplicationDetailPageWrapper
            applications={applications}
            onToggleStage={toggleStage}
            onUpdateStages={updateStages}
            onUpdateCompensation={updateCompensation}
            onUpdateStatus={updateStatus}
            onUpdateNote={updateNote}
            onUpdateDetails={updateDetails}
            onArchive={archiveApplication}
            onUnarchive={unarchiveApplication}
            onSubmitReject={rejectApplication}
            onToggleFavorite={toggleFavorite}
            settings={settings}
            plan={effectivePlan}
          />
        }
      />
      <Route
        path="/profile"
        element={
          <ProfilePage
            userId={sessionUserId}
            email={sessionEmail}
            avatarUrl={avatarUrl}
            displayName={settings.displayName}
            onAvatarChange={(_url: string | null, path: string | null) => {
              setAvatarFromPath(path ?? null)
            }}
            onDisplayNameChange={(name: string | null) => {
              void handleSaveSettings({ ...settings, displayName: name })
            }}
          />
        }
      />
      <Route
        path="/settings"
        element={
          <SettingsPage
            settings={settings}
            isSaving={isSavingSettings}
            isLoading={isLoadingSettings}
            error={settingsError}
            onSave={handleSaveSettings}
            userEmail={sessionEmail}
            onDeleteAccount={handleDeleteAccount}
            plan={effectivePlan}
          />
        }
      />
      <Route
        path="/billing"
        element={
          <BillingPage
            userId={sessionUserId!}
            subscription={subscription}
            activeAppCount={activeApps.length}
            resumeAnalysesCount={resumeAnalysesCount}
            quizzesCount={quizzesCount}
            dateFormat={settings.dateFormat}
            onRefreshSubscription={refreshSubscription}
          />
        }
      />
      <Route
        path="/pricing"
        element={
          <PricingPage
            userId={sessionUserId!}
            isPro={isPro}
          />
        }
      />
      <Route
        path="/resume"
        element={
          <ResumePage
            userId={sessionUserId!}
            plan={effectivePlan}
          />
        }
      />
      <Route
        path="/resume/comparison/:comparisonId"
        element={
          <ResumeComparisonDetailPage
            userId={sessionUserId!}
          />
        }
      />
      <Route
        path="/resume/:analysisId"
        element={
          <ResumeAnalysisDetailPage
            userId={sessionUserId!}
          />
        }
      />
      <Route
        path="/quiz"
        element={
          <QuizPage
            userId={sessionUserId!}
            plan={effectivePlan}
            applications={applications}
            dateFormat={settings.dateFormat}
          />
        }
      />
      <Route
        path="/quiz/:id"
        element={
          <QuizResultPage
            userId={sessionUserId!}
            plan={effectivePlan}
          />
        }
      />
      <Route path="/analytics" element={<AnalyticsPage />} />
      <Route
        path="/interview"
        element={
          <MockInterviewPage
            userId={sessionUserId!}
            plan={effectivePlan}
            applications={applications}
            dateFormat={settings.dateFormat}
          />
        }
      />
      <Route
        path="/interview/:id"
        element={
          <MockInterviewResultPage
            userId={sessionUserId!}
          />
        }
      />
      <Route
        path="/chat"
        element={
          <AIChatPage
            userId={sessionUserId!}
            plan={effectivePlan}
            applications={applications}
            dateFormat={settings.dateFormat}
          />
        }
      />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/help" element={<HelpPage onStartProductTour={onStartProductTour} />} />
      <Route path="/extension/connect" element={<ExtensionConnectPage />} />
      <Route path="/extension/callback" element={<ExtensionCallbackPage />} />
      <Route
        path="/resume-builder"
        element={
          <ResumeBuilderListPage
            userId={sessionUserId!}
            plan={effectivePlan}
            displayName={settings.displayName}
            email={sessionEmail}
          />
        }
      />
      <Route
        path="/resume-builder/:id"
        element={
          <ResumeBuilderEditorPage
            userId={sessionUserId!}
            plan={effectivePlan}
            applications={applications}
          />
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}
